// Centralised GTM / GA4 dataLayer utility.
// All window.dataLayer pushes and window._bmkSession counters go through here.

function dl() {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
}

function session() {
    window._bmkSession = window._bmkSession || {};
    return window._bmkSession;
}

/** Raw push — use named helpers below whenever possible. */
export function pushDataLayer(payload) {
    dl().push(payload);
}

/**
 * Increments a named session counter and syncs the new value to dataLayer.
 * Returns the updated count.
 */
export function incrementSession(key) {
    const s = session();
    s[key] = (s[key] || 0) + 1;
    dl().push({ [key]: s[key] });
    return s[key];
}

/** Returns the current value of a session counter without incrementing. */
export function getSession(key) {
    return (window._bmkSession && window._bmkSession[key]) || 0;
}

// ── Page context ──────────────────────────────────────────

/** Pushes the current page name (used by NavBar on step change). */
export function pushStepPageName(stepName) {
    dl().push({ page_name: stepName });
}

/**
 * Pushes the full funnel context on every SPA route change.
 * Undefined values are omitted so stale DLV keys aren't overwritten.
 * current_step is the configurator sub-step: 'addons' | 'landpride' | 'summary' | 'quote'.
 */
export function pushPageContext({ page_name, category_name, series_name, model_name, current_step } = {}) {
    dl().push({
        page_name,
        ...(category_name  !== undefined && { category_name }),
        ...(series_name    !== undefined && { series_name }),
        ...(model_name     !== undefined && { model_name }),
        ...(current_step   !== undefined && { current_step }),
    });
}

/** Pushes auth_status so GTM's DLV - auth_status is always current. */
export function pushAuthStatus(isAuthenticated) {
    dl().push({ auth_status: isAuthenticated ? 'authenticated' : 'unauthenticated' });
}

/** Pushes dealer_name when a dealer is selected on the Summary page. */
export function pushDealerName(dealerName) {
    dl().push({ dealer_name: dealerName || '' });
}

/** Pushes total_price before quote_sent fires so the DLV is populated. */
export function pushTotalPrice(totalPrice) {
    dl().push({ total_price: totalPrice });
}

// ── Navigation ────────────────────────────────────────────

/** Fires nav_interaction for horizontal scroll on the progress bar. */
export function trackNavScroll({ direction, current_step }) {
    dl().push({
        event: 'nav_interaction',
        interaction_type: 'horizontal_scroll',
        scroll_direction: direction,
        current_step,
    });
}

// ── Funnel events ─────────────────────────────────────────

/** Fires configurator_started on first entry to the configurator. */
export function trackConfiguratorStarted() {
    dl().push({ event: 'configurator_started' });
}

/** Pushes category_name and primary_action_taken on a Start-page click. */
export function trackCategoryClick(categoryName) {
    dl().push({ category_name: categoryName, primary_action_taken: true });
}

/** Fires series_clicked with all required parameters. */
export function trackSeriesClicked({ series_name, click_position, category_name, session_series_clicks }) {
    dl().push({
        event: 'series_clicked',
        series_name,
        click_position,
        category_name,
        session_series_clicks,
        primary_action_taken: true,
    });
}

/** Fires series_detail_opened when a series Details panel opens. */
export function trackSeriesDetailOpened({ series_name, details_opened_in_session }) {
    dl().push({
        event: 'series_detail_opened',
        series_name,
        details_opened_in_session,
    });
}

/** Fires model_picker_opened when a model card's package picker opens. */
export function trackModelPickerOpened({ model_name, picker_opens_in_session }) {
    dl().push({
        event: 'model_picker_opened',
        model_name,
        picker_opens_in_session,
        primary_action_taken: true,
    });
}

/**
 * Fires model_package_selected when Start Configuring is clicked.
 * price_match is pushed first so the DLV is set before the event tag fires.
 */
export function trackModelPackageSelected(price_match) {
    dl().push({ price_match });
    dl().push({ event: 'model_package_selected', primary_action_taken: true });
}

// ── Configurator add-on events ────────────────────────────

/** Fires addon_warning_interacted with stage:'shown' when a warning modal renders. */
export function trackAddonWarningShown({ warning_type, warning_addon_name, conflicting_addon, required_addon }) {
    dl().push({
        event: 'addon_warning_interacted',
        stage: 'shown',
        warning_stage: 'shown',
        warning_type,
        warning_addon_name,
        ...(conflicting_addon && { conflicting_addon }),
        ...(required_addon   && { required_addon }),
    });
}

/** Updates warning_stage to 'resolved' before a resolution button's callback fires. */
export function pushWarningResolved() {
    dl().push({ warning_stage: 'resolved' });
}

/** Fires landpride_addon_selected when a Landpride add-on is confirmed. */
export function trackLandprideAddonSelected({ landpride_addon_name, landpride_addons_count, modal_required }) {
    dl().push({
        event: 'landpride_addon_selected',
        landpride_addon_name,
        landpride_addons_count,
        modal_required,
    });
}

// ── Summary & conversion events ───────────────────────────

/** Fires build_printed with the chosen print type ('overview' | 'detailed'). */
export function trackBuildPrinted(print_type) {
    dl().push({ event: 'build_printed', print_type });
}

/** Fires quote_sent — the primary GA4 Conversion event. */
export function trackQuoteSent() {
    dl().push({ event: 'quote_sent' });
}

/** Fires build_saved after a build is successfully saved to the user's account. */
export function trackBuildSaved() {
    dl().push({ event: 'build_saved' });
}

/** Fires build_shared when the share flow is initiated. */
export function trackBuildShared() {
    dl().push({ event: 'build_shared' });
}

/**
 * Fires start_over_confirmed when the user clicks "Yes, Start Over" in the
 * confirmation modal. Must be called before navigating away.
 */
export function trackStartOverConfirmed() {
    dl().push({ event: 'start_over_confirmed' });
}

// ── Configurator engagement events ────────────────────────

/**
 * Fires addon_selection_required when an "Additional Selection Required" modal
 * opens (stage: 'shown') or the user confirms their selection (stage: 'confirmed').
 * selection_type: 'quantity' for MaxQty > 1 pickers, 'include_required' for
 * IncludesOneOf / Includes / TireCompat rule modals.
 */
export function trackAddonSelectionRequired({ addon_name, selection_type, stage, max_quantity } = {}) {
    dl().push({
        event: 'addon_selection_required',
        addon_name,
        selection_type,
        stage,
        ...(max_quantity !== undefined && { max_quantity }),
    });
}

/** Fires model_specs_viewed when the specs modal is opened. */
export function trackModelSpecsViewed(model_name) {
    dl().push({ event: 'model_specs_viewed', model_name });
}

/**
 * Fires media_interacted for 3D-viewer library callbacks.
 * Gallery clicks are handled by GTM data attributes — no JS push needed there.
 */
export function trackMediaInteracted({ media_type, interaction_type, image_index } = {}) {
    dl().push({
        event: 'media_interacted',
        media_type,
        interaction_type,
        ...(image_index !== undefined && { image_index }),
    });
}

/** Fires static_image_scroll_attempted when a touchmove is detected on the static image. */
export function trackStaticImageScrollAttempted() {
    dl().push({ event: 'static_image_scroll_attempted' });
}
